import { FC, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import axios from "../../../utils/axios";

// Batch picker for a given SKU + source location — lists batches that currently
// hold stock there (from /erp/stock/levels). Empty for non-batch SKUs.
interface Props {
  skuId?: string;
  locationId?: string;
  value?: string;
  onChange: (batchId: string) => void;
  disabled?: boolean;
  // Label for the saved batch when the source's stock can't be listed (e.g. receiver without source access).
  fallbackLabel?: string;
}

const BatchSelect: FC<Props> = ({ skuId, locationId, value, onChange, disabled, fallbackLabel }) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    let active = true;
    if (!skuId || !locationId) {
      setOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/api/v1/erp/stock/levels?skuId=${skuId}&locationId=${locationId}&limit=200`);
        // /stock/levels returns a paginated { data, total }; tolerate a bare array too.
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const opts = list
          .filter((lvl: any) => lvl.batchId && (lvl.quantity ?? 0) > 0)
          .map((lvl: any) => ({
            label: `${lvl.batchId?.batchNumber || lvl.batchId} (qty ${lvl.quantity})`,
            value: lvl.batchId?._id || lvl.batchId,
          }));
        if (active) setOptions(opts);
      } catch {
        if (active) setOptions([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [skuId, locationId]);

  const listed = options.find((o) => o.value === value);
  const fallback = !listed && value && fallbackLabel ? { label: fallbackLabel, value } : null;
  const allOptions = fallback ? [fallback, ...options] : options;
  const selected = listed || fallback || null;

  return (
    <Autocomplete
      options={allOptions}
      disabled={disabled}
      getOptionLabel={(o: any) => o.label || ""}
      isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
      value={selected}
      onChange={(_e, v: any) => onChange(v ? v.value : "")}
      renderInput={(params) => <TextField {...params} label="Batch" size="small" />}
    />
  );
};

export default BatchSelect;
